'use client';

import {
  cloneElement,
  isValidElement,
  memo,
  type ElementType,
  type ReactNode,
} from 'react';

import { type VariantProps } from 'tailwind-variants';

import {
  ButtonBase,
  type ButtonBaseSharedProps,
  type SpinnerSizeValue,
} from '../../internal/components/button-base/ButtonBase';
import { useLoadingAnnouncement } from '../../internal/hooks/useLoadingAnnouncement';
import { type Elevation } from '../../internal/styles/elevation';
import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';
import { Spinner } from '../spinner/Spinner';

type Variant = VariantProps<typeof iconButtonStyles>;

type IconButtonBaseProps = ButtonBaseSharedProps & {
  /** @default 'ghost' */
  variant?: ButtonBaseSharedProps['variant'];
  /**
   * Icon element rendered inside the button.
   */
  children: ReactNode;
  /**
   * Accessible name for the button. Required because the component has no
   * visible text label.
   */
  'aria-label': string;
  /**
   * Container border-radius preset. `rounded` uses the standard radius token;
   * `circle` applies a fully rounded radius.
   * @default 'circle'
   */
  shape?: Variant['shape'];
  /**
   * Elevation depth. Applies a drop-shadow and, for non-filled variants, an elevated surface colour. Omit to apply no elevation.
   */
  elevation?: Elevation;
};

const DEFAULT_ELEMENT_TYPE = 'button';

const iconButtonClasses = {
  root: 'zds-icon-button-root',
  icon: 'zds-icon-button-icon',
  spinner: 'zds-icon-button-spinner',
} as const;

const iconButtonStyles = tv({
  slots: {
    root: [iconButtonClasses.root],
    icon: [iconButtonClasses.icon, 'text-inherit'],
    spinner: [iconButtonClasses.spinner, 'text-inherit'],
  },
  variants: {
    size: {
      sm: {
        root: ['w-[var(--z--button-base-sm-height)]'],
        icon: ['z--button-base-sm-icon-height', 'z--button-base-sm-icon-width'],
      },
      md: {
        root: ['w-[var(--z--button-base-md-height)]'],
        icon: ['z--button-base-md-icon-height', 'z--button-base-md-icon-width'],
      },
      lg: {
        root: ['w-[var(--z--button-base-lg-height)]'],
        icon: ['z--button-base-lg-icon-height', 'z--button-base-lg-icon-width'],
      },
      xl: {
        root: ['w-[var(--z--button-base-xl-height)]'],
        icon: ['z--button-base-xl-icon-height', 'z--button-base-xl-icon-width'],
      },
    },
    sizeXs: {
      sm: {
        root: ['xs:w-[var(--z--button-base-sm-height)]'],
        icon: [
          'xs:z--button-base-sm-icon-height',
          'xs:z--button-base-sm-icon-width',
        ],
      },
      md: {
        root: ['xs:w-[var(--z--button-base-md-height)]'],
        icon: [
          'xs:z--button-base-md-icon-height',
          'xs:z--button-base-md-icon-width',
        ],
      },
      lg: {
        root: ['xs:w-[var(--z--button-base-lg-height)]'],
        icon: [
          'xs:z--button-base-lg-icon-height',
          'xs:z--button-base-lg-icon-width',
        ],
      },
      xl: {
        root: ['xs:w-[var(--z--button-base-xl-height)]'],
        icon: [
          'xs:z--button-base-xl-icon-height',
          'xs:z--button-base-xl-icon-width',
        ],
      },
    },
    sizeSm: {
      sm: {
        root: ['sm:w-[var(--z--button-base-sm-height)]'],
        icon: [
          'sm:z--button-base-sm-icon-height',
          'sm:z--button-base-sm-icon-width',
        ],
      },
      md: {
        root: ['sm:w-[var(--z--button-base-md-height)]'],
        icon: [
          'sm:z--button-base-md-icon-height',
          'sm:z--button-base-md-icon-width',
        ],
      },
      lg: {
        root: ['sm:w-[var(--z--button-base-lg-height)]'],
        icon: [
          'sm:z--button-base-lg-icon-height',
          'sm:z--button-base-lg-icon-width',
        ],
      },
      xl: {
        root: ['sm:w-[var(--z--button-base-xl-height)]'],
        icon: [
          'sm:z--button-base-xl-icon-height',
          'sm:z--button-base-xl-icon-width',
        ],
      },
    },
    sizeMd: {
      sm: {
        root: ['md:w-[var(--z--button-base-sm-height)]'],
        icon: [
          'md:z--button-base-sm-icon-height',
          'md:z--button-base-sm-icon-width',
        ],
      },
      md: {
        root: ['md:w-[var(--z--button-base-md-height)]'],
        icon: [
          'md:z--button-base-md-icon-height',
          'md:z--button-base-md-icon-width',
        ],
      },
      lg: {
        root: ['md:w-[var(--z--button-base-lg-height)]'],
        icon: [
          'md:z--button-base-lg-icon-height',
          'md:z--button-base-lg-icon-width',
        ],
      },
      xl: {
        root: ['md:w-[var(--z--button-base-xl-height)]'],
        icon: [
          'md:z--button-base-xl-icon-height',
          'md:z--button-base-xl-icon-width',
        ],
      },
    },
    sizeLg: {
      sm: {
        root: ['lg:w-[var(--z--button-base-sm-height)]'],
        icon: [
          'lg:z--button-base-sm-icon-height',
          'lg:z--button-base-sm-icon-width',
        ],
      },
      md: {
        root: ['lg:w-[var(--z--button-base-md-height)]'],
        icon: [
          'lg:z--button-base-md-icon-height',
          'lg:z--button-base-md-icon-width',
        ],
      },
      lg: {
        root: ['lg:w-[var(--z--button-base-lg-height)]'],
        icon: [
          'lg:z--button-base-lg-icon-height',
          'lg:z--button-base-lg-icon-width',
        ],
      },
      xl: {
        root: ['lg:w-[var(--z--button-base-xl-height)]'],
        icon: [
          'lg:z--button-base-xl-icon-height',
          'lg:z--button-base-xl-icon-width',
        ],
      },
    },
    sizeXl: {
      sm: {
        root: ['xl:w-[var(--z--button-base-sm-height)]'],
        icon: [
          'xl:z--button-base-sm-icon-height',
          'xl:z--button-base-sm-icon-width',
        ],
      },
      md: {
        root: ['xl:w-[var(--z--button-base-md-height)]'],
        icon: [
          'xl:z--button-base-md-icon-height',
          'xl:z--button-base-md-icon-width',
        ],
      },
      lg: {
        root: ['xl:w-[var(--z--button-base-lg-height)]'],
        icon: [
          'xl:z--button-base-lg-icon-height',
          'xl:z--button-base-lg-icon-width',
        ],
      },
      xl: {
        root: ['xl:w-[var(--z--button-base-xl-height)]'],
        icon: [
          'xl:z--button-base-xl-icon-height',
          'xl:z--button-base-xl-icon-width',
        ],
      },
    },
    // shape declared after size variants so circle's rounded-full wins over ButtonBase's responsive radius via twMerge
    shape: {
      rounded: { root: [] },
      circle: {
        root: [
          'rounded-full',
          'xs:rounded-full',
          'sm:rounded-full',
          'md:rounded-full',
          'lg:rounded-full',
          'xl:rounded-full',
        ],
      },
    },
  },
});

type IconButtonContentProps = {
  isFocused: boolean;
  isLoading: boolean;
  elementId: string;
  ariaLabelledby: string | undefined;
  loadingIndicatorId: string;
  loadingAriaLabel: string | undefined;
  styles: ReturnType<typeof iconButtonStyles>;
  spinnerSize: ResponsiveOption<SpinnerSizeValue>;
  children: ReactNode;
};

const IconButtonContent = memo(
  ({
    isFocused,
    isLoading,
    elementId,
    ariaLabelledby,
    loadingIndicatorId,
    loadingAriaLabel,
    styles,
    spinnerSize,
    children,
  }: IconButtonContentProps) => {
    useLoadingAnnouncement({ elementId, isLoading, isFocused, ariaLabelledby });

    return isLoading ? (
      <Spinner
        id={loadingIndicatorId}
        className={styles.spinner()}
        color="inherit"
        size={spinnerSize}
        aria-label={loadingAriaLabel}
      />
    ) : isValidElement(children) ? (
      cloneElement(children, {
        className: styles.icon({
          class: (children.props as { className?: string }).className,
        }),
      } as Partial<typeof children.props>)
    ) : (
      children
    );
  }
);

IconButtonContent.displayName = 'IconButtonContent';

type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;

type OmittedKeys = 'disabled';

type IconButtonProps<RootComponent extends ElementType = DefaultElementType> =
  PolymorphicComponentProps<IconButtonBaseProps, RootComponent, OmittedKeys>;

type IconButtonComponent = PolymorphicComponent<
  IconButtonBaseProps,
  DefaultElementType,
  OmittedKeys
>;

const IconButton: IconButtonComponent = (props) => {
  // Cast to IconButtonProps to access all properties including those from the
  // polymorphic component type. The PolymorphicComponent generic infers a union
  // of possible prop types, so we narrow it here, assuming the default 'button'
  // element type.
  const finalProps = props as IconButtonProps;
  const {
    id,
    component: Root = DEFAULT_ELEMENT_TYPE,
    variant = 'ghost',
    color = 'accent-primary',
    shape = 'circle',
    size: sizeProp,
    elevation,
    isDisabled = false,
    isLoading = false,
    loadingAriaLabel,
    children,
    className,
    ref,
    ...rest
  } = finalProps;

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const resolvedSize = defaultSize ?? 'md';

  const styles = iconButtonStyles({
    shape,
    size: resolvedSize,
    sizeXs: responsiveSize?.xs,
    sizeSm: responsiveSize?.sm,
    sizeMd: responsiveSize?.md,
    sizeLg: responsiveSize?.lg,
    sizeXl: responsiveSize?.xl,
  });

  return (
    <ButtonBase
      {...rest}
      id={id}
      component={Root}
      variant={variant}
      color={color}
      size={sizeProp}
      elevation={elevation}
      isDisabled={isDisabled}
      isLoading={isLoading}
      loadingAriaLabel={loadingAriaLabel}
      componentName="IconButton"
      ref={ref}
      className={styles.root({ class: className })}
    >
      {({
        isFocused,
        rootId,
        loadingIndicatorId,
        ariaLabelledby,
        spinnerSize,
      }) => (
        <IconButtonContent
          isFocused={isFocused}
          isLoading={isLoading}
          elementId={rootId}
          ariaLabelledby={ariaLabelledby}
          loadingIndicatorId={loadingIndicatorId}
          loadingAriaLabel={loadingAriaLabel}
          styles={styles}
          spinnerSize={spinnerSize}
        >
          {children}
        </IconButtonContent>
      )}
    </ButtonBase>
  );
};

IconButton.displayName = 'IconButton';

export {
  IconButton,
  type IconButtonProps,
  type IconButtonBaseProps,
  iconButtonClasses,
};
